import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  RESEARCH_SUPPORT_BASE,
  STATUS_LABELS,
  STATUS_COLORS,
  SUBMISSION_STATUSES,
} from '../../constants/researchSupport';
import { fetchUserSubmissions } from '../../services/researchSubmissionService';
import LoadingIndicator from '../common/LoadingIndicator';

function MySubmissions() {
  const { user, loading: authLoading } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const data = await fetchUserSubmissions(user.uid);
        setSubmissions(data.filter((s) => s.status !== SUBMISSION_STATUSES.DRAFT));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (authLoading) return <LoadingIndicator />;
  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{ from: `${RESEARCH_SUPPORT_BASE}/my-submissions` }}
        replace
      />
    );
  }

  if (loading) return <LoadingIndicator />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-gray-600 text-sm">
          Track review progress, feedback, and timeline updates for your submissions.
        </p>
        <Link
          to={`${RESEARCH_SUPPORT_BASE}/apply`}
          className="inline-flex justify-center px-5 py-2 bg-iof text-white rounded-full text-sm font-medium hover:bg-iof-dark"
        >
          New application
        </Link>
      </div>

      {submissions.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600 mb-4">You have not submitted any research projects yet.</p>
          <Link
            to={`${RESEARCH_SUPPORT_BASE}/apply`}
            className="text-iof font-medium hover:underline"
          >
            Start your first application →
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <ul className="space-y-3">
            {submissions.map((sub) => (
              <li key={sub.id}>
                <button
                  type="button"
                  onClick={() => setSelected(sub)}
                  className={`w-full text-left p-4 rounded-lg border transition-colors ${
                    selected?.id === sub.id
                      ? 'border-iof bg-iof/5'
                      : 'border-gray-200 bg-white hover:border-iof/40'
                  }`}
                >
                  <p className="font-medium text-gray-900 line-clamp-1">{sub.researchTitle}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Submitted {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : '—'}
                  </p>
                  <span
                    className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${
                      STATUS_COLORS[sub.status] || 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {STATUS_LABELS[sub.status] || sub.status}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {selected && (
            <article className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">{selected.researchTitle}</h2>
              <p className="text-sm text-gray-500 mb-4">ID: {selected.id}</p>

              {selected.reviewerNotes && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
                  <h3 className="text-sm font-semibold text-amber-900 mb-1">Reviewer feedback</h3>
                  <p className="text-sm text-amber-800 whitespace-pre-wrap">{selected.reviewerNotes}</p>
                </div>
              )}

              <h3 className="text-sm font-semibold text-gray-800 mb-2">Timeline</h3>
              <ol className="space-y-2">
                {(selected.timeline || []).map((entry, i) => (
                  <li key={`${entry.at}-${i}`} className="text-sm border-l-2 border-iof/30 pl-3">
                    <span className="font-medium">{STATUS_LABELS[entry.status] || entry.status}</span>
                    <span className="text-gray-500 block text-xs">
                      {entry.at ? new Date(entry.at).toLocaleString() : ''}
                    </span>
                    {entry.note && <span className="text-gray-600">{entry.note}</span>}
                  </li>
                ))}
              </ol>

              {selected.status === SUBMISSION_STATUSES.REVISION_REQUESTED && (
                <Link
                  to={`${RESEARCH_SUPPORT_BASE}/apply`}
                  className="inline-block mt-4 text-iof font-medium hover:underline"
                >
                  Upload revisions via application portal →
                </Link>
              )}
            </article>
          )}
        </div>
      )}
    </div>
  );
}

export default MySubmissions;
